"use client"

import { cn } from "@/lib/utils"

/**
 * A short, closed set of choices as one bordered group — canvas 7c's
 * Q3 / Q2 / Year / All.
 *
 * Distinct from `Segmented` in `field.tsx`, which is a *form field*: a label
 * over words on an underline, for a value being written. This is a control on a
 * toolbar, for a view being narrowed, and it reads as one object because the
 * choices are exclusive.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: readonly { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
  /** Names the group for a screen reader — "Period", say. */
  label: string
  className?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "inline-flex flex-none items-center overflow-hidden rounded-md border",
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "px-3 py-2 text-[11.5px] leading-none transition-colors",
              selected
                ? "bg-primary/[0.08] font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
